import { BufferAttribute, InstancedBufferGeometry, Sphere, Vector3 } from 'three';

/**
 * Jedno stéblo trávy v lokálním prostoru.
 *
 * Souřadnice jsou normalizované: y jde od 0 (kořen) do 1 (špička), x je
 * ±0.5 a ve vertex shaderu se násobí skutečnou šířkou. `aSide` (-1 vlevo,
 * 0 uprostřed, +1 vpravo) slouží k zaoblení normály.
 *
 * Tvar má tři vlastnosti, na kterých stojí, jestli to vypadá jako tráva
 * nebo jako drát:
 *
 *  1. **Šířka podle mocniny, ne lineárně.** Skutečné stéblo drží šířku
 *     skoro po celé délce a zúží se až v poslední třetině. Původní zúžení
 *     `1 - t²·0.85` ubíralo šířku hned od kořene a stéblo se četlo jako
 *     protáhlý klín.
 *  2. **Špička je krátká hrana, ne bod.** Jediný vrchol na konci dá ostrou
 *     jehlu; skutečné stéblo má konec zaoblený a zblízka to hodně změní.
 *  3. **Žlábek se dělá normálou, ne geometrií.** Prohnutí stébla se čte
 *     z toho, jak po jeho šířce putuje odlesk — a to zvládne posunutá
 *     normála (viz `aSide` ve vertex shaderu) bez jediného vrcholu navíc.
 *     Řada vrcholů uprostřed by počet trojúhelníků zdvojnásobila, a to je
 *     u statisíců stébel cena, kterou tvar nestojí.
 */
export function buildBladeGeometry(segments: number, instanceCount: number): InstancedBufferGeometry {
  const rows = segments + 1;
  const vertexCount = rows * 2;
  const positions = new Float32Array(vertexCount * 3);
  const sides = new Float32Array(vertexCount);
  const heights = new Float32Array(vertexCount);
  const indices: number[] = [];

  for (let row = 0; row < rows; row++) {
    const t = row / segments;
    // Plná šířka skoro po celé délce, zúžení až v poslední třetině.
    const width = 0.5 * (1 - Math.pow(t, 2.6) * 0.92);
    for (let col = 0; col < 2; col++) {
      const v = row * 2 + col;
      positions[v * 3] = col === 0 ? -width : width;
      positions[v * 3 + 1] = t;
      positions[v * 3 + 2] = 0;
      sides[v] = col === 0 ? -1 : 1;
      heights[v] = t;
    }
  }

  for (let row = 0; row < segments; row++) {
    const a = row * 2;
    const b = (row + 1) * 2;
    indices.push(a, b, a + 1, a + 1, b, b + 1);
  }

  const geometry = new InstancedBufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('aSide', new BufferAttribute(sides, 1));
  geometry.setAttribute('aHeight', new BufferAttribute(heights, 1));
  // Normály přepisuje vertex shader, ale atribut musí existovat, jinak
  // MeshStandardMaterial neprojde kompilací.
  geometry.setAttribute('normal', new BufferAttribute(new Float32Array(vertexCount * 3).fill(0), 3));
  geometry.setIndex(indices);
  geometry.instanceCount = instanceCount;

  // Culling řešíme sami — pole trávy je vždy kolem kamery.
  geometry.boundingSphere = new Sphere(new Vector3(), 1e6);

  return geometry;
}
